import { useCallback, useEffect, useMemo, useState } from 'react'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { notFound } from '@tanstack/react-router'
import { Request } from 'commons/utils/request'
import { isEqual } from 'lodash'
import { TableSortingChangeHandler, TableSortingOrder } from 'ui/components'

import { ContactDetailsRequest, ContactDetailsResponse } from '../types'

const defaultFilter = (id: number): ContactDetailsRequest => ({
  alerts: '',
  frequency: 'all',
  internal: null,
  page: 1,
  size: 10,
  sortBy: '',
  sortDesc: true,
  historyPage: 1,
  historySize: 10,
  id
})

export const useGetContactDetails = (id: number) => {
  const [filter, setFilter] = useState<ContactDetailsRequest>(() => defaultFilter(id))

  useEffect(() => {
    setFilter(defaultFilter(id))
  }, [id])

  const [updateCaller, setUpdateCaller] = useState<'' | 'documents' | 'history'>('')

  const resetPending = useCallback(() => setUpdateCaller(''), [])

  const { isFetching, data, isFetched, refetch, isSuccess, error } = useQuery<
    ContactDetailsRequest,
    Error,
    ContactDetailsResponse
  >({
    queryFn: () => Request.post(Request.urls.bank.contactDetailsWebPage, filter),
    queryKey: ['get-contact', filter],
    placeholderData: keepPreviousData
  })

  useEffect(() => {
    if (isSuccess && data) {
      resetPending()
    }
    if (error) {
      resetPending()
    }
  }, [isSuccess, data, error])

  if ((isFetched && !data) || !id) {
    throw notFound()
  }

  const onDocumentsFilterChange = useCallback(
    (docFilter: ContactDetailsRequest) => {
      if (!isEqual({ ...filter, ...docFilter }, filter)) {
        setFilter({ ...filter, ...docFilter })
        setUpdateCaller('documents')
      }
    },
    [filter]
  )

  const onOrderChange: TableSortingChangeHandler = useCallback(
    (value) => {
      setFilter({
        ...filter,
        sortBy: value.field,
        sortDesc: value.direction === 'DESC'
      })
      setUpdateCaller('documents')
    },
    [filter]
  )

  const order: TableSortingOrder = useMemo(
    () => ({
      field: filter?.sortBy ?? '',
      direction: filter?.sortDesc ? 'DESC' : 'ASC'
    }),
    [filter]
  )

  const onHistoryFilterChange = useCallback(
    (page: number, size: number) => {
      if (page !== filter.historyPage || size !== filter.historySize) {
        setFilter({ ...filter, historyPage: page, historySize: size })
        setUpdateCaller('history')
      }
    },
    [filter]
  )

  const historyTableConfig = useMemo(
    () => ({
      isFetching: updateCaller === 'history',
      filter: {
        page: filter.historyPage,
        size: filter.historySize
      },
      onFilterChange: onHistoryFilterChange
    }),
    [filter, onHistoryFilterChange, updateCaller]
  )

  const documentsTableConfig = useMemo(
    () => ({
      onOrderChange,
      isFetching: updateCaller === 'documents',
      onFilterChange: onDocumentsFilterChange,
      order: [order]
    }),
    [onOrderChange, updateCaller, onDocumentsFilterChange, order]
  )

  return {
    isFetched,
    isFetching,
    data,
    refetch,
    historyTableConfig,
    documentsTableConfig
  }
}
